import React from "react";
import { motion } from "framer-motion";
import image from "../../assets/Images/banneriamge.jpg";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const Banner = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isTamil = i18n.language === "ta";

  return (
    <section className="bg-[#012b47] text-white relative overflow-hidden py-8 sm:py-16 md:py-24">
      {/* Floating circle (top-right) */}
      <motion.div
        className="absolute -top-32 -right-32 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-[#0891b2] rounded-full opacity-20 hidden sm:block"
        animate={{ y: [0, 20, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Dotted pattern (bottom-left) */}
      <motion.div
        className="absolute bottom-6 sm:bottom-12 left-10 sm:left-10 w-32 sm:w-72 h-32 sm:h-72 opacity-40 hidden sm:block"
        animate={{ y: [0, -15, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      >
        <svg width="100%" height="100%">
          <defs>
            <pattern
              id="dots"
              x="0"
              y="0"
              width="26"
              height="26"
              patternUnits="userSpaceOnUse"
            >
              <circle cx="20" cy="16" r="3" fill="#22c5dc" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dots)" />
        </svg>
      </motion.div>

      {/* Main Container */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-2">
        <div className="grid grid-cols-1 lg:grid-cols-2 text-center lg:text-left gap-10 lg:gap-12 items-center">
          {/* Text Section */}
          <motion.div
            className="space-y-5 sm:space-y-6"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1 }}
          >
            <h1 className="font-bold leading-snug sm:leading-tight space-y-3 relative">
              {/* Title 1 */}
              <span
                className={`block f6 ${
                  isTamil
                    ? "text-lg sm:text-2xl md:text-3xl lg:text-4xl"
                    : "text-3xl sm:text-5xl md:text-6xl lg:text-8xl"
                }`}
              >
                {t("banner.title1")}
              </span>

              {/* Blue circle */}
              <span
                className="absolute top-8 left-1/2 sm:top-10 sm:left-72 -translate-x-1/2 
                w-6 h-6 sm:w-10 sm:h-10 md:w-20 md:h-20 
                bg-[#22c5dc] rounded-full opacity-70 -z-10 hidden sm:block"
              />

              {/* Title 2 */}
              <span
                className={`block f6 ${
                  isTamil
                    ? "text-lg sm:text-2xl md:text-3xl lg:text-4xl"
                    : "text-3xl sm:text-5xl md:text-6xl lg:text-8xl"
                }`}
              >
                {t("banner.title2")}
              </span>

              {/* Title 3 */}
              <span
                className={`block font-extrabold tracking-tight f6 ${
                  isTamil
                    ? "text-lg sm:text-2xl md:text-3xl lg:text-4xl"
                    : "text-3xl sm:text-5xl md:text-6xl lg:text-8xl"
                }`}
              >
                {t("banner.title3")}
              </span>
            </h1>

            {/* Buttons */}
            <motion.div
              className="flex flex-row justify-center lg:justify-start flex-wrap gap-3 sm:gap-4 pt-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
            >
              <button
                className={`bg-white text-[#012b47] font-semibold rounded-full shadow-lg hover:bg-gray-100 transition transform hover:scale-105 
${
  isTamil
    ? "text-sm sm:text-base px-5 py-2 sm:px-6 sm:py-2.5"
    : "text-base sm:text-lg px-6 py-3 sm:px-8 sm:py-3"
}`}
                onClick={() => navigate("/selftest")}
              >
                {t("banner.btn1")}
              </button>

              <button
                className={`bg-white text-[#012b47] font-semibold rounded-full shadow-lg hover:bg-gray-100 transition transform hover:scale-105 
${
  isTamil
    ? "text-sm sm:text-base px-5 py-2 sm:px-3 sm:py-2.5"
    : "text-base sm:text-lg px-6 py-3 sm:px-8 sm:py-3"
}`}
                onClick={() => navigate("/contact")}
              >
                {t("banner.btn2")}
              </button>
            </motion.div>
          </motion.div>

          {/* Image Section */}
          <motion.div
            className="flex justify-center lg:justify-end mt-6 lg:mt-0"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <div className="relative">
              <div
                className="relative rounded-2xl overflow-hidden shadow-2xl 
                max-w-[280px] sm:max-w-sm md:max-w-md lg:max-w-lg mx-auto 
                sm:border sm:border-[#22c5dc] sm:opacity-80 sm:rounded-[1rem]"
              >
                <img
                  src={image}
                  alt="People celebrating"
                  className="w-full h-auto object-cover rounded-2xl"
                />
                <div className="absolute -inset-1 md:-inset-2 border border-[#22c5dc] md:border-2 rounded-[1rem] opacity-70 hidden sm:block"></div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Banner;
